/**
 * Carrier Performance Service
 * 
 * Provides analytics and metrics for evaluating carrier performance.
 * Metrics include:
 * 1. Reservation Revision Rate
 * 2. On-Time Arrival Rate
 * 3. Plate Accuracy Rate
 * 4. Driver Accuracy Rate
 */

import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { COLLECTIONS } from '../../constants/collections';
import { getReservationsByDateRange } from './reservationService';
import { getGeneralSettings } from '../firestore/systemConfigService';

// --- DEFAULT CONFIG FALLBACKS ---
const DEFAULT_WEIGHTS = {
    ON_TIME: 0.4,    // 40%
    ACCURACY: 0.3,   // 30% (Plate + Driver)
    REVISION: 0.2,   // 20%
    COMPLETION: 0.1  // 10%
};

const DEFAULT_TOLERANCE = 15;

/**
 * Get Carrier Performance Statistics
 */
export const getCarrierPerformanceStats = async (tenantId, filters = {}) => {
    try {
        const { startDate, endDate, transporterId } = filters;

        // 1. Fetch System Config for Weights
        const settings = await getGeneralSettings(tenantId);
        const weights = {
            onTime: (settings?.onTimeWeight || 40) / 100,
            accuracy: (settings?.accuracyWeight || 30) / 100,
            revision: (settings?.revisionWeight || 20) / 100,
            completion: (settings?.completionWeight || 10) / 100
        };
        const toleranceMinutes = settings?.toleranceMinutes || DEFAULT_TOLERANCE;

        // 2. Fetch Reservations for the period
        const reservations = await getReservationsByDateRange(tenantId, startDate, endDate);

        if (reservations.length === 0) {
            return {
                summary: getEmptySummary(),
                carriers: []
            };
        }

        // 3. Fetch Audit Logs
        const auditLogs = await fetchAuditLogsForPeriod(tenantId, startDate, endDate);

        // 4. Group by Transporter
        const carrierStats = {};

        reservations.forEach(res => {
            // Filter by transporter if selected
            if (transporterId && res.transporterId !== transporterId) return;

            const tId = res.transporterId || 'UNKNOWN';
            const tName = res.transporterName || 'Bilinmiyor';

            if (!carrierStats[tId]) {
                carrierStats[tId] = {
                    id: tId,
                    name: tName,
                    totalReservations: 0,
                    totalEntered: 0,
                    onTimeCount: 0,
                    revisedCount: 0,
                    correctPlateCount: 0,
                    correctDriverCount: 0,
                    reservations: []
                };
            }

            carrierStats[tId].totalReservations++;
            carrierStats[tId].reservations.push(res);

            if (res.entryTime || res.status === 'facility_entered' || res.status === 'completed') {
                carrierStats[tId].totalEntered++;
            }
        });

        // 5. Calculate Metrics per Carrier
        const carriersData = Object.values(carrierStats).map(carrier => {
            const CarrierLogs = auditLogs.filter(log =>
                carrier.reservations.some(r => r.id === log.metadata?.reservationId || log.entityId === r.id)
            );

            // A. Revision Rate
            const revisedReservations = carrier.reservations.filter(res => {
                // If status is explicitly revised, count it
                if (res.status?.toLowerCase().includes('revize')) return true;

                // Check audit logs
                return CarrierLogs.some(log =>
                    (log.action === 'TIME_CHANGE' || log.action === 'UPDATE') &&
                    log.metadata?.reservationId === res.id &&
                    (log.metadata?.changes?.startTime || log.metadata?.timeChange)
                );
            });
            const revisionRate = carrier.totalReservations > 0
                ? ((carrier.totalReservations - revisedReservations.length) / carrier.totalReservations) * 100
                : 0;

            // B. On-Time & Delayed Calculation
            let onTimeCount = 0;
            let delayedCount = 0;
            const now = new Date();
            const enteredReservations = carrier.reservations.filter(r => r.entryTime || r.status === 'completed' || r.status === 'facility_entered');

            carrier.reservations.forEach(res => {
                const planned = res.startTime instanceof Date ? res.startTime : new Date(res.startTime);

                // If entered
                if (res.entryTime || res.status === 'completed' || res.status === 'facility_entered') {
                    const actual = res.entryTime instanceof Date ? res.entryTime : new Date(res.entryTime);
                    if (!isNaN(actual.getTime())) {
                        const diffMins = (actual - planned) / (1000 * 60);
                        if (diffMins <= toleranceMinutes) {
                            onTimeCount++;
                        } else {
                            delayedCount++;
                        }
                    }
                } else if (res.status !== 'cancelled' && res.status !== 'rejected') {
                    // Not entered yet - check if scheduled time passed
                    // Add 15 mins buffer before calling it delayed if not entered
                    if (now > new Date(planned.getTime() + (toleranceMinutes * 60000))) {
                        delayedCount++;
                    }
                }
            });

            // CRITICAL FIX: If 0 arrivals, on-time rate is 0%, not 100%
            const onTimeRate = enteredReservations.length > 0
                ? (onTimeCount / enteredReservations.length) * 100
                : 0;

            // C. Accuracy Rates (Plate & Driver)
            let correctPlateCount = 0;
            let correctDriverCount = 0;

            const badDataPlaceholders = ['belirsiz', 'yok', 'plaka', 'tanımlanmadı', 'none', '-'];

            carrier.reservations.forEach(res => {
                const plate = (res.plateNumber || '').toLowerCase();
                const driver = (res.driverName || '').toLowerCase();

                const isPlatePoor = !plate || badDataPlaceholders.some(p => plate.includes(p));
                const isDriverPoor = !driver || badDataPlaceholders.some(p => driver.includes(p));

                const hasPlateLog = CarrierLogs.some(log =>
                    log.metadata?.reservationId === res.id &&
                    (log.action === 'PLATE_CHANGE' || log.metadata?.changes?.plateNumber)
                );
                const hasDriverLog = CarrierLogs.some(log =>
                    log.metadata?.reservationId === res.id &&
                    (log.action === 'DRIVER_CHANGE' || log.metadata?.changes?.driver)
                );

                if (!isPlatePoor && !hasPlateLog) correctPlateCount++;
                if (!isDriverPoor && !hasDriverLog) correctDriverCount++;
            });

            const plateAccuracy = carrier.totalReservations > 0
                ? (correctPlateCount / carrier.totalReservations) * 100
                : 0;
            const driverAccuracy = carrier.totalReservations > 0
                ? (correctDriverCount / carrier.totalReservations) * 100
                : 0;

            // D. Overall Score (0-10)
            // Weighting only applied if there's enough data
            const arrivalWeight = enteredReservations.length > 0 ? weights.onTime : 0;
            const accuracyWeight = weights.accuracy;
            const revisionWeight = weights.revision;

            // Re-normalize weights if some are 0
            const totalActiveWeight = arrivalWeight + accuracyWeight + revisionWeight;

            const rawScore = totalActiveWeight > 0 ? (
                (onTimeRate * arrivalWeight) +
                (((plateAccuracy + driverAccuracy) / 2) * accuracyWeight) +
                (revisionRate * revisionWeight)
            ) / totalActiveWeight : 0;

            const score = rawScore / 10;

            // E. Volume Occupancy Rate (Calculation based on Reservation Capacity vs Actual)
            // Note: In this context, we'll assume occupancy is related to how much of the vehicle was filled vs requested
            // If actual weight/volume data isn't in audit logs, we'll use a placeholder logic that calculates 
            // the ratio of 'Completed' vs 'Cancelled' or 'Revised' volume impact, 
            // or simply the average occupancy field from reservations if available.
            const totalVolume = carrier.reservations.reduce((sum, r) => sum + (parseFloat(r.volume || 0)), 0);
            const actualVolume = carrier.reservations
                .filter(r => r.status === 'completed')
                .reduce((sum, r) => sum + (parseFloat(r.volume || 0)), 0);

            const volumeOccupancyRate = totalVolume > 0 ? (actualVolume / totalVolume) * 100 : 0;

            return {
                ...carrier,
                metrics: {
                    revisionRate,
                    onTimeRate,
                    plateAccuracy,
                    driverAccuracy,
                    score: parseFloat(score.toFixed(1)),
                    volumeOccupancyRate: parseFloat(volumeOccupancyRate.toFixed(1))
                },
                raw: {
                    revised: revisedReservations.length,
                    onTime: onTimeCount,
                    delayed: delayedCount,
                    totalEntered: enteredReservations.length,
                    cancelled: carrier.reservations.filter(r => r.status === 'cancelled' || r.status === 'rejected').length,
                    plateChanged: carrier.totalReservations - correctPlateCount,
                    driverChanged: carrier.totalReservations - correctDriverCount
                }
            };
        });

        // 5. Calculate Summary Stats (Averages)
        const summary = calculateSummary(carriersData);

        return {
            summary,
            carriers: carriersData.sort((a, b) => b.metrics.score - a.metrics.score) // Sort by score desc
        };

    } catch (error) {
        console.error("Carrier performance stats error:", error);
        throw error;
    }
};

// --- HELPERS ---

const getEmptySummary = () => ({
    averageScore: 0,
    totalReservations: 0,
    avgOnTimeRate: 0,
    avgAccuracyRate: 0,
    avgRevisionRate: 0,
    avgVolumeOccupancy: 0
});

const calculateSummary = (carriers) => {
    if (carriers.length === 0) return getEmptySummary();

    const totalRes = carriers.reduce((sum, c) => sum + c.totalReservations, 0);
    const avgScore = carriers.reduce((sum, c) => sum + c.metrics.score, 0) / carriers.length;

    // Weighted averages could be better, but simple average for now
    const avgOnTime = carriers.reduce((sum, c) => sum + c.metrics.onTimeRate, 0) / carriers.length;
    const avgRev = carriers.reduce((sum, c) => sum + c.metrics.revisionRate, 0) / carriers.length;
    const avgAcc = carriers.reduce((sum, c) => sum + ((c.metrics.plateAccuracy + c.metrics.driverAccuracy) / 2), 0) / carriers.length;
    const avgVolume = carriers.reduce((sum, c) => sum + c.metrics.volumeOccupancyRate, 0) / carriers.length;

    return {
        averageScore: parseFloat(avgScore.toFixed(1)),
        totalReservations: totalRes,
        avgOnTimeRate: parseFloat(avgOnTime.toFixed(1)),
        avgAccuracyRate: parseFloat(avgAcc.toFixed(1)),
        avgRevisionRate: parseFloat(avgRev.toFixed(1)),
        avgVolumeOccupancy: parseFloat(avgVolume.toFixed(1))
    };
};

const fetchAuditLogsForPeriod = async (tenantId, startDateStr, endDateStr) => {
    // Audit logs might be large. We need an efficient way.
    // Assuming 'audit_logs' collection has 'timestamp' field.
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    end.setHours(23, 59, 59, 999);

    const q = query(
        collection(db, `tenants/${tenantId}/${COLLECTIONS.AUDIT_LOGS}`),
        where('timestamp', '>=', Timestamp.fromDate(start)),
        where('timestamp', '<=', Timestamp.fromDate(end))
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};
